// src/App.tsx
import React from 'react';
import { BrowserRouter, Routes, Route, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AnimatedBackground } from './components/AnimatedBackground';
import { Navigation } from './components/Navigation';
import { Hero } from './components/Hero';
import { CoursesSection } from './components/CoursesSection';
import { AboutPage } from './components/AboutPage';
import { ContactPage } from './components/ContactPage';
import { Footer } from './components/Footer';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { SignInPage } from './pages/SignInPage';
import { AccountPage } from './pages/AccountPage';
import { TeachingChatbot } from './components/TeachingChatbot';
import { CourseViewer } from './components/pages/CourseViewer';
import StudyChatPage from './pages/StudyChatPage';
import { Privacy } from './components/pages/Privacy';
import { TermsOfService } from './components/pages/terms';

export default function App() {
  return (
    <BrowserRouter>
      {/* Sticky footer shell */}
        <AnimatedBackground />

        <div className="relative z-10 flex flex-col min-h-0 flex-1">
          <AuthProvider>
            <Routes>
              {/* popout chat route */}
              <Route
                path="/study-chat/popout"
                element={
                  <PopoutShell>
                    <StudyChatPage popout />
                  </PopoutShell>
                }
              />

              {/* standalone chat routes */}
              <Route path="/study" element={<StudyChatPage />} />
              <Route path="/study/:subjectId" element={<StudyChatPage />} />

              {/* Main site layout with nav/footer/chatbot */}
              <Route element={<LandingShell />}>
                {/* / → Home */}
                <Route index element={<HomePage />} />

                {/* /courses, /classroom, /about, /contact */}
                <Route path="courses" element={<CoursesSection />} />
                <Route path="classroom" element={<CourseViewer />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="terms" element={<TermsOfService />} />
                {/* /signin */}
                <Route
                  path="signin"
                  element={
                    <section className="auth-shell">
                      <SignInRoute />
                    </section>
                  }
                />

                {/* /account (protected) */}
                <Route
                  path="account"
                  element={
                    <section className="auth-shell">
                      <AccountGate>
                        <AccountPage />
                      </AccountGate>
                    </section>
                  }
                />

                {/* optional: catch-all → home */}
                <Route path="*" element={<HomePage />} />
              </Route>
            </Routes>
          </AuthProvider>
        </div>

        <Toaster position="bottom-right" theme="dark" />
    </BrowserRouter>
  );
}

function PopoutShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#070E1C] text-white">
      <AnimatedBackground />
      <div className="relative z-10 min-h-screen flex flex-col">{children}</div>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

/** Layout: nav + outlet + footer + chatbot */
function LandingShell() {
  const location = useLocation();
  const navigate = useNavigate();

  // keep your ?go=signin behavior, but now route instead of setting local state
  React.useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const go = sp.get('go');
    if (go) {
      if (go === 'signin') navigate('/signin', { replace: true });
      if (go === 'account') navigate('/account', { replace: true });

      sp.delete('go');
      navigate(
        { search: sp.toString() ? `?${sp.toString()}` : '' },
        { replace: true }
      );
    }
  }, [location.search, navigate]);

  return (
    <>
      <Navigation />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <TeachingChatbot />
    </>
  );
}

/** Home page wrapper so we can use useNavigate inside */
function HomePage() {
  const navigate = useNavigate();
  return <Hero onExploreCourses={() => navigate('/courses')} />;
}

function AccountGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="py-24 text-center text-cyan-200">Loading…</div>;
  if (!user) return <div className="py-24 text-center text-cyan-200">Please sign in.</div>;
  return <>{children}</>;
}

/** Signin wrapper that implements your old `go()` callback via navigation */
function SignInRoute() {
  const navigate = useNavigate();

  return (
    <SignInPage
      go={(page) => {
        switch (page) {
          case 'account':
            navigate('/account');
            break;
          case 'courses':
            navigate('/courses');
            break;
          case 'classroom':
            navigate('/classroom');
            break;
          case 'about':
            navigate('/about');
            break;
          case 'contact':
            navigate('/contact');
            break;
          case 'home':
          default:
            navigate('/');
        }
      }}
    />
  );
}
